/**
 * DownsizeStrategyAllocator.js – allocate sale proceeds from downsizing (RAD, new home, downsizer, gifts, etc.).
 * Used by AgedCareAnalyser. Props: factFind, details, setDetails, radRoomCost (optional modelled room / RAD cap for slider + input cap).
 */
import React, { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Building, Home, Wallet, Gift, ShieldCheck, TrendingUp as TrendingUpIcon } from 'lucide-react';
import { CurrencyInput, AllocationInput } from '../../components/ui/index.tsx';
import StampDutyFields from '../../components/ui/StampDutyFields.tsx';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import { DOWNSIZER_CONTRIBUTION_CAP, GIFTING_LIMIT_ANNUAL, FUNERAL_BOND_LIMIT } from '../../constants/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import DownsizerRulesAndInsights from '../../components/strategy/DownsizerRulesAndInsights.tsx';
import { strategyExplainers } from '../../strategy/strategyExplainers.ts';

/** Sale-proceeds allocation for the "downsize the family home" strategy (manual control / suggestion engine). */
export interface DownsizeDetails {
    salePrice?: number | string;
    sellingCosts?: number | string;
    radAllocation?: number | string;
    newHomePurchasePrice?: number | string;
    /** Duty on the new home — its own line, never folded into selling costs (owner, 23 Sep 2026). */
    stampDuty?: number | string;
    /** The state the new home is in, for the duty estimate. */
    newHomeState?: string;
    downsizerContribution?: number | string;
    partnerDownsizerContribution?: number | string;
    commenceIncomeStream?: boolean;
    purchaseInvestmentProperty?: number | string;
    investmentPropertyRent?: number | string;
    investmentPropertyExpenses?: number | string;
    purchaseShares?: number | string;
    giftingAmount?: number | string;
    funeralBondPurchase?: number | string;
    [key: string]: unknown;
}

export interface DownsizeStrategyAllocatorProps {
    factFind: DraftFactFind;
    details: DownsizeDetails;
    setDetails: Dispatch<SetStateAction<DownsizeDetails>>;
    radRoomCost?: number;
}

function DownsizeStrategyAllocator({ factFind, details, setDetails, radRoomCost = 0 }: DownsizeStrategyAllocatorProps) {
    // The duty on the new home comes off the proceeds beside the selling costs (23 Sep 2026).
    const netProceeds = (Number(details.salePrice) || 0) - (Number(details.sellingCosts) || 0) - (Number(details.stampDuty) || 0);
    const radCap = Math.max(0, Number(radRoomCost) || 0);
    const radSliderMax = radCap > 0 ? Math.min(netProceeds, radCap) : netProceeds;

    const handleDetailChange = (field: string, value: number | string | undefined) => {
        if (field === 'radAllocation') {
            const num = value === '' || value === undefined ? 0 : Number(value);
            const capped = Number.isFinite(num) ? Math.min(Math.max(0, num), radSliderMax) : 0;
            setDetails(prev => ({ ...prev, radAllocation: capped }));
            return;
        }
        // The state is the one field that is a word, not a figure (owner-caught, 23 Sep 2026: the
        // select "always reverts" — 'NSW' had been parsed to 0).
        if (field === 'newHomeState') {
            setDetails(prev => ({ ...prev, newHomeState: String(value ?? '') }));
            return;
        }
        setDetails(prev => ({ ...prev, [field]: parseFloat(String(value)) || 0 }));
    };

    useEffect(() => {
        setDetails(prev => {
            const r = Number(prev.radAllocation) || 0;
            if (r <= radSliderMax) return prev;
            return { ...prev, radAllocation: radSliderMax };
        });
    }, [radSliderMax, setDetails]);

    const totalAllocated = (Number(details.radAllocation) || 0) +
        (Number(details.newHomePurchasePrice) || 0) +
        (Number(details.downsizerContribution) || 0) +
        (Number(details.partnerDownsizerContribution) || 0) +
        (Number(details.giftingAmount) || 0) +
        (Number(details.funeralBondPurchase) || 0) +
        (Number(details.purchaseInvestmentProperty) || 0) +
        (Number(details.purchaseShares) || 0);
    const unallocatedProceeds = netProceeds - totalAllocated;

    const clientAge = calculateAge(factFind.client?.dob);
    const isDownsizerEligible = (clientAge ?? 0) >= 55;

    return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-6">
            <div>
                <h4 className="font-semibold text-lg">1. Calculate Net Proceeds</h4>
                {/* `md:` measures the viewport, not this column: three columns fired at a 768px
                    window while the allocator sat in a 619px working column — ~185px per currency
                    field, which is narrower than "$3,500,000" renders. Two columns until `2xl:`,
                    where the working column is wide enough for three. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4 mt-2">
                    <CurrencyInput label="Sale Price of Home" name="salePrice" value={details.salePrice || ''} onChange={(v) => handleDetailChange('salePrice', v)} />
                    <CurrencyInput label="Selling Costs" name="sellingCosts" value={details.sellingCosts || ''} onChange={(v) => handleDetailChange('sellingCosts', v)} />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Net Proceeds</label>
                        <p className="w-full px-4 py-2 bg-gray-200 border border-gray-300 rounded-lg font-bold">{formatCurrency(netProceeds)}</p>
                    </div>
                </div>
            </div>
            <div>
                <h4 className="font-semibold text-lg">2. Allocate the Proceeds</h4>
                <div className="mt-2 p-3 bg-white border-l-4 border-blue-400 rounded-r-lg text-sm flex justify-between items-center">
                    <p><strong className="text-blue-700">Unallocated Proceeds:</strong></p>
                    <p className={`font-bold text-lg ${unallocatedProceeds < 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(unallocatedProceeds)}</p>
                </div>
                <div className="mt-4 space-y-4">
                    <AllocationInput icon={Building} label="Fund the RAD" name="radAllocation" value={details.radAllocation} onChange={handleDetailChange} max={radSliderMax} />
                    <AllocationInput icon={Home} label="Purchase New Home" name="newHomePurchasePrice" value={details.newHomePurchasePrice} onChange={handleDetailChange} max={netProceeds} />
                    {(Number(details.newHomePurchasePrice) || 0) > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-white p-3">
                            <StampDutyFields
                                state={details.newHomeState}
                                onStateChange={(v) => handleDetailChange('newHomeState', v)}
                                newHomePrice={details.newHomePurchasePrice}
                                duty={details.stampDuty}
                                onDutyChange={(v) => handleDetailChange('stampDuty', v)}
                            />
                        </div>
                    )}
                    <AllocationInput
                        icon={Wallet}
                        label="Downsizer Contribution"
                        name="downsizerContribution"
                        value={details.downsizerContribution}
                        onChange={handleDetailChange}
                        max={DOWNSIZER_CONTRIBUTION_CAP}
                        disabled={!isDownsizerEligible}
                        tooltip={strategyExplainers.downsizerToSuper(isDownsizerEligible)}
                    />
                    <DownsizerRulesAndInsights factFind={factFind} />
                    {factFind.adviceFor === 'couple' && (
                        <div className="mt-2">
                            <AllocationInput
                                icon={Wallet}
                                label="Partner's Downsizer Contribution"
                                name="partnerDownsizerContribution"
                                value={details.partnerDownsizerContribution}
                                onChange={handleDetailChange}
                                max={DOWNSIZER_CONTRIBUTION_CAP}
                                disabled={!isDownsizerEligible}
                                tooltip={strategyExplainers.partnerDownsizerToSuper(isDownsizerEligible)}
                            />
                        </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                        <input type="checkbox" id="commenceIncomeStream" checked={!!details.commenceIncomeStream} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDetails(prev => ({ ...prev, commenceIncomeStream: e.target.checked }))} />
                        <label htmlFor="commenceIncomeStream" className="text-sm">Commence an income stream from downsizer contribution</label>
                    </div>
                    <AllocationInput icon={Building} label="Buy Investment Property" name="purchaseInvestmentProperty" value={details.purchaseInvestmentProperty} onChange={handleDetailChange} max={netProceeds} tooltip={strategyExplainers.buyInvestmentProperty()} />
                    {(Number(details.purchaseInvestmentProperty) || 0) > 0 && (
                        <div className="ml-6 pl-4 border-l-2 border-gray-200 space-y-2">
                            <CurrencyInput label="Assumed annual rental income ($)" name="investmentPropertyRent" value={details.investmentPropertyRent ?? ''} onChange={(v) => handleDetailChange('investmentPropertyRent', v)} />
                            <CurrencyInput label="Assumed annual expenses ($)" name="investmentPropertyExpenses" value={details.investmentPropertyExpenses ?? ''} onChange={(v) => handleDetailChange('investmentPropertyExpenses', v)} />
                            <p className="text-xs text-gray-600">Investment property expenses (e.g. rates, insurance, maintenance) are generally tax deductible.</p>
                        </div>
                    )}
                    <AllocationInput icon={TrendingUpIcon} label="Buy shares" name="purchaseShares" value={details.purchaseShares} onChange={handleDetailChange} max={netProceeds} tooltip={strategyExplainers.buyShares()} />
                    <AllocationInput icon={Gift} label="Gift to Loved Ones" name="giftingAmount" value={details.giftingAmount} onChange={handleDetailChange} max={netProceeds} tooltip={strategyExplainers.giftToLovedOnes()} />
                    <AllocationInput icon={ShieldCheck} label="Purchase Funeral Bond" name="funeralBondPurchase" value={details.funeralBondPurchase} onChange={handleDetailChange} max={FUNERAL_BOND_LIMIT} tooltip={strategyExplainers.funeralBond()} />
                </div>
            </div>

            {/* Step 3 — the ledger. Owner's ask (2026-08-24): "it's not clear that I'm funding the
                RAD with some of that money … where did the freed money go?" The inputs above answer
                one destination at a time; nothing summed the story. This reads THE SAME `details`
                the inputs write and the engine consumes (see the analyser's simulatedFactFind
                downsize branch), so it cannot diverge from either — and each destination carries
                its means-test consequence, the transposed Insight Panel knowledge, as a chip. */}
            {netProceeds > 0 && (
                <div>
                    <h4 className="font-semibold text-lg">3. Where every dollar goes</h4>
                    <div className="mt-2 rounded-lg border border-slate-200 bg-white px-4 py-3">
                        <FlowRow label="Sale of the home" amount={Number(details.salePrice) || 0} />
                        <FlowRow label="Selling costs" amount={-(Number(details.sellingCosts) || 0)} />
                        <FlowRow label="Net proceeds" amount={netProceeds} bold divider />
                        {ledgerRows(details).map((row) => (
                            <FlowRow key={row.label} label={row.label} amount={-row.amount} chip={row.chip} chipTone={row.tone} note={row.note} />
                        ))}
                        <FlowRow
                            label="Remainder — stays in the bank as Unallocated Sale Proceeds"
                            amount={unallocatedProceeds}
                            bold
                            divider
                            chip={unallocatedProceeds > 0 ? 'deemed & counted' : undefined}
                            chipTone="amber"
                        />
                        {unallocatedProceeds < 0 && (
                            <p className="mt-2 text-xs font-semibold text-red-600">
                                Allocated {formatCurrency(-unallocatedProceeds)} more than the sale frees up — reduce an allocation above.
                            </p>
                        )}
                        {unallocatedProceeds > 0 && (
                            <p className="mt-2 text-xs text-gray-500">
                                Anything left unallocated sits in the bank, where it is deemed and counted by both the
                                Age Pension and aged care means tests. The exempt destinations above shelter more of it.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/** One line of the money-flow ledger. */
function FlowRow({ label, amount, bold = false, divider = false, chip, chipTone, note }: {
    label: string; amount: number; bold?: boolean; divider?: boolean;
    chip?: string; chipTone?: 'green' | 'amber'; note?: string;
}) {
    return (
        <div className={divider ? 'border-t border-slate-200 pt-1.5 mt-1.5' : ''}>
            <div className="flex items-baseline justify-between gap-3 py-0.5">
                <span className={`text-sm ${bold ? 'font-bold text-slate-900' : 'text-slate-600'}`}>
                    {label}
                    {chip && (
                        <span className={`ml-2 align-middle rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${chipTone === 'green' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {chip}
                        </span>
                    )}
                </span>
                <span className={`text-sm tabular-nums ${bold ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'}`}>
                    {formatCurrency(amount)}
                </span>
            </div>
            {note && <p className="text-xs text-amber-700 -mt-0.5 pb-1">{note}</p>}
        </div>
    );
}

/** The non-zero destinations, each with its means-test consequence. */
function ledgerRows(details: DownsizeDetails): { label: string; amount: number; chip: string; tone: 'green' | 'amber'; note?: string }[] {
    const rows: { label: string; amount: number; chip: string; tone: 'green' | 'amber'; note?: string }[] = [];
    const add = (amount: unknown, label: string, chip: string, tone: 'green' | 'amber', note?: string) => {
        const value = Number(amount) || 0;
        if (value > 0) rows.push({ label, amount: value, chip, tone, note });
    };
    add(details.radAllocation, 'Fund the RAD (refundable room deposit)', 'exempt from assets test', 'green');
    add(details.newHomePurchasePrice, 'Purchase new home (new principal residence)', 'exempt', 'green');
    add(details.downsizerContribution, 'Downsizer contribution to super', 'counted once in pension phase', 'amber');
    add(details.partnerDownsizerContribution, "Partner's downsizer contribution", 'counted once in pension phase', 'amber');
    add(details.purchaseInvestmentProperty, 'Buy investment property', 'fully counted, both tests', 'amber');
    add(details.purchaseShares, 'Buy shares', 'counted & deemed', 'amber');
    const gift = Number(details.giftingAmount) || 0;
    add(details.giftingAmount, 'Gift to loved ones',
        gift > GIFTING_LIMIT_ANNUAL ? 'partly still counted' : 'within free area', gift > GIFTING_LIMIT_ANNUAL ? 'amber' : 'green',
        gift > GIFTING_LIMIT_ANNUAL
            ? `${formatCurrency(gift - GIFTING_LIMIT_ANNUAL)} is beyond the ${formatCurrency(GIFTING_LIMIT_ANNUAL)} yearly free area — Centrelink still counts it for 5 years (deprivation).`
            : undefined);
    add(details.funeralBondPurchase, 'Funeral bond', 'exempt up to the limit', 'green');
    return rows;
}

export default DownsizeStrategyAllocator;
